import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  is_archived: boolean;
  action_url: string | null;
  action_label: string | null;
  created_at: string;
  read_at: string | null;
}

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .rpc('get_unread_notification_count');

      if (error) throw error;

      setUnreadCount(data || 0);
    } catch (error) {
      logger.error('Error loading unread count', error);
    }
  }, [user]);

  // Load recent notifications
  const loadNotifications = useCallback(async (limit = 10) => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('is_archived', false)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      logger.error('Error loading notifications', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Mark as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .rpc('mark_notification_read', { p_notification_id: notificationId });

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );

      // Update count
      await loadUnreadCount();
    } catch (error) {
      logger.error('Error marking notification as read', error);
      throw error;
    }
  }, [loadUnreadCount]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read');

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );

      setUnreadCount(0);
    } catch (error) {
      logger.error('Error marking all as read', error);
      throw error;
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_archived: true })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Update count if it was unread
      await loadUnreadCount();
    } catch (error) {
      logger.error('Error deleting notification', error);
      throw error;
    }
  }, [loadUnreadCount]);

  // Subscribe to new notifications
  useEffect(() => {
    if (!user) return;

    // Initial load
    loadUnreadCount();

    // Subscribe to changes
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug('New notification received', payload);
          loadUnreadCount();

          // Optionally reload notifications if they're being displayed
          if (notifications.length > 0) {
            loadNotifications(notifications.length);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          loadUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, loadUnreadCount, loadNotifications, notifications.length]);

  return {
    unreadCount,
    notifications,
    loading,
    loadNotifications,
    loadUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
